<template>
  <div class="mb-6">
    <div class="flex flex-col sm:flex-row gap-4 sm:overflow-x-auto pb-4">
      <div
        v-for="(status, sindex) in statuses"
        :key="sindex"
        class="w-full sm:w-80 sm:min-w-[280px] lg:min-w-[320px] bg-[#F8FAFC] rounded-2xl sm:rounded-[32px] border border-[#E2E8F0] p-3 sm:p-4 flex flex-col flex-shrink-0 transition-colors duration-200"
        :class="{
          'bg-blue-50 border-blue-300': dragOverColumn === status.status,
          'bg-[#F8FAFC]': dragOverColumn !== status.status
        }"
        @dragover.prevent="handleDragOver(status.status)"
        @dragleave="handleDragLeave"
        @drop="handleDrop($event, status.status)"
        @touchmove.prevent="handleTouchMove"
        @touchend="handleTouchEnd"
      >
        <div class="flex items-center justify-between mb-3">
          <div class="flex items-center gap-2">
            <span :class="['inline-block shrink-0 w-2 h-2 aspect-square rounded-full', getColorBgForId(status.status)]"></span>
            <span class="text-xl font-extrabold text-gray-800">{{ status.name }}</span>
            <span class="text-xl font-extrabold text-[#94A3B8]">({{ groupedLeads[status.name]?.length || 0 }})</span>
          </div>
        </div>

        <div 
          class="flex flex-col gap-4 sm:max-h-[548px] sm:min-h-[548px] sm:overflow-y-auto sm:pr-1"
          :data-status="status.status"
        >
          <!--Cards-->
          <div
            v-for="(lead, lidx) in groupedLeads[status.name] || []"
            :key="lead.id || lidx"
            class="bg-white rounded-lg p-4 border border-[#E2E8F0] cursor-grab hover:shadow-sm flex-shrink-0 duration-200 select-none"
            :class="{
              'opacity-50 cursor-grabbing': draggedLead?.id === lead.id,
              'cursor-pointer': !isDragging
            }"
            draggable="true"
            @dragstart="handleDragStart($event, lead)"
            @dragend="handleDragEnd"
            @click="!isDragging && handleLeadClick(lead)"
            @touchstart="handleTouchStart($event, lead)"
            @touchmove.prevent="handleTouchMove"
            @touchend="handleTouchEnd"
          >
            <div>
              <span 
                :class="[
                  'inline-block font-medium text-xs px-2 py-1 rounded-md', 
                  getBadgeBgForId(lead.status?.id), 
                  getBadgeTextForId(lead.status?.id)
                ]"
              >
                {{ lead.status?.name || status.name }}
              </span>
            </div>
            <div class="mt-3">
              <div class="text-base font-semibold text-gray-800">
                {{ getFullName(lead) }}
              </div>
              <div class="text-sm font-medium text-[#475569] mt-1">
                Teléfono: <span class="font-bold text-sm text-gray-700">{{ lead.phone || 'Sin teléfono' }}</span>
              </div>
            </div>
          </div>
          
          <div 
            v-if="(groupedLeads[status.name] || []).length == 0" 
            class="text-sm text-gray-400 text-center py-8"
          >
            No hay prospectos
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps, defineEmits, ref } from 'vue'
import axios from 'axios'
import { useStore } from 'vuex'

// Store
const store = useStore()
const session = computed(() => store.getters.getUserSession)

// Props
const props = defineProps({
  leads: {
    type: Array,
    default: () => [],
    required: true
  },
  statuses: {
    type: Array,
    default: () => [
      { name: 'Pendiente de contactar', status: '1' },
      { name: 'En proceso', status: '2' },
      { name: 'Finalizado', status: '3' },
      { name: 'No aplica', status: '4' },
      { name: 'Perdido', status: '5' },
      { name: 'Descartado', status: '6' },
    ]
  }
})

// Events
const emit = defineEmits(['lead-click', 'status-updated', 'update-error'])

// Drag and drop state
const draggedLead = ref(null)
const dragOverColumn = ref(null)
const isDragging = ref(false)
const touchStartPosition = ref({ x: 0, y: 0 })
const isTouchDragging = ref(false)

// Color mappings
const bgMap = {
  '1': 'bg-green-800',
  '2': 'bg-yellow-400',
  '3': 'bg-cyan-600',
  '4': 'bg-violet-900',
  '5': 'bg-orange-400',
  '6': 'bg-red-500',
}

const badgeBgMap = {
  '1': 'bg-green-100',
  '2': 'bg-yellow-100',
  '3': 'bg-cyan-100',
  '4': 'bg-violet-100',
  '5': 'bg-orange-100',
  '6': 'bg-red-100',
}

const badgeTextMap = {
  '1': 'text-green-800',
  '2': 'text-yellow-800',
  '3': 'text-cyan-800',
  '4': 'text-violet-800',
  '5': 'text-orange-800',
  '6': 'text-red-800',
}

// Computed properties
const groupedLeads = computed(() => {
  const map = {}
  props.statuses.forEach((status) => {
    map[status.name] = props.leads.filter((lead) => 
      String(lead.status?.id) === String(status.status)
    )
  })
  return map
})

// Methods
const getColorBgForId = (id) => {
  return bgMap[String(id)] || 'bg-gray-500'
}

const getBadgeBgForId = (id) => {
  return badgeBgMap[String(id)] || 'bg-gray-100'
}

const getBadgeTextForId = (id) => {
  return badgeTextMap[String(id)] || 'text-gray-800'
}

const getFullName = (lead) => {
  const parts = [lead.name, lead.last_name, lead.second_last_name].filter(Boolean)
  return parts.join(' ') || 'Sin nombre'
}

const handleLeadClick = (lead) => {
  emit('lead-click', lead)
}

// Drag and Drop Functions
const handleDragStart = (event, lead) => {
  draggedLead.value = lead
  isDragging.value = true
  event.dataTransfer.effectAllowed = 'move'
  event.dataTransfer.setData('text/plain', lead.id)
  
  // Añadir clase visual al elemento arrastrado
  event.target.style.opacity = '0.5'
}

const handleDragEnd = (event) => {
  isDragging.value = false
  draggedLead.value = null
  dragOverColumn.value = null
  event.target.style.opacity = '1'
}

const handleDragOver = (statusId) => {
  dragOverColumn.value = statusId
}

const handleDragLeave = () => {
  dragOverColumn.value = null
}

const handleDrop = async (event, newStatusId) => {
  event.preventDefault()
  
  if (!draggedLead.value || String(draggedLead.value.status?.id) === String(newStatusId)) {
    return
  }
  
  await updateLeadStatus(draggedLead.value.id, newStatusId)
  
  draggedLead.value = null
  dragOverColumn.value = null
}

// Touch Events for Mobile Support
const handleTouchStart = (event, lead) => {
  const touch = event.touches[0]
  touchStartPosition.value = { x: touch.clientX, y: touch.clientY }
  
  // Detectar si es un movimiento de drag después de un pequeño delay
  setTimeout(() => {
    if (!isTouchDragging.value) {
      draggedLead.value = lead
      isTouchDragging.value = true
      
      // Crear efecto visual
      event.target.style.opacity = '0.7'
      event.target.style.transform = 'scale(1.05)'
    }
  }, 150)
}

const handleTouchMove = (event) => {
  if (!draggedLead.value && !isTouchDragging.value) return
  
  const touch = event.touches[0]
  const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY)
  
  // Encontrar la columna sobre la que está el dedo
  const columnElement = elementBelow?.closest('[data-status]')
  if (columnElement) {
    const statusId = columnElement.getAttribute('data-status')
    dragOverColumn.value = statusId
  }
}

const handleTouchEnd = async (event) => {
  if (!draggedLead.value || !isTouchDragging.value) {
    isTouchDragging.value = false
    return
  }
  
  const touch = event.changedTouches[0]
  const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY)
  const columnElement = elementBelow?.closest('[data-status]')
  
  if (columnElement) {
    const newStatusId = columnElement.getAttribute('data-status')
    
    if (String(draggedLead.value.status?.id) !== String(newStatusId)) {
      await updateLeadStatus(draggedLead.value.id, newStatusId)
    }
  }
  
  // Limpiar estado
  draggedLead.value = null
  dragOverColumn.value = null
  isTouchDragging.value = false
  
  // Restaurar estilos
  if (event.target) {
    event.target.style.opacity = '1'
    event.target.style.transform = 'scale(1)'
  }
}

// API Call to update lead status
const updateLeadStatus = async (leadId, newStatusId) => {
  let url = `${process.env.VUE_APP_API_BASE_URL}/broker/lead/${leadId}/update_status`;

  var options = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.value.token_api}`,
    },
  }
  try {
    let param_post = { "status": newStatusId };

    const response = await axios.put(url, param_post, options);
    if (response.status === 200) {
      // Emitir evento para que el componente padre actualice la lista
      emit('status-updated', { leadId, newStatusId, lead: draggedLead.value })
    }
  } catch (error) {
    //console.error('Error updating lead status:', error)
    
    //emit('update-error', errorMessage)
  }
}
</script>

<style scoped>

</style>